import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Star, Lightning, MapPin } from "@phosphor-icons/react";
import { AppBadges } from "./AppBadges";

const lines = ["Maracás na", "palma da mão."];

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.15 + i * 0.12 },
  }),
};

const chips = [
  { label: "Lanches", top: "12%", left: "-6%", color: "bg-maraca-amber", delay: 0 },
  { label: "Açaí", top: "34%", right: "-10%", color: "bg-maraca-pink", delay: 0.4 },
  { label: "Farmácia", bottom: "22%", left: "-9%", color: "bg-maraca-teal", delay: 0.8 },
  { label: "Mercado", bottom: "6%", right: "-4%", color: "bg-maraca-navy text-white", delay: 1.2 },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="topo"
      ref={ref}
      className="relative overflow-hidden bg-white pt-32 md:pt-40"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -left-40 -top-40 h-96 w-96 rounded-full bg-maraca-teal/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 top-20 h-80 w-80 rounded-full bg-maraca-amber/20 blur-3xl" />

      <div className="mx-auto grid max-w-8xl items-center gap-12 px-5 pb-16 md:grid-cols-2 md:px-10 md:pb-24">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-maraca-navy/10 bg-maraca-teal/10 px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-widest text-maraca-navy"
          >
            <MapPin size={16} weight="fill" className="text-maraca-teal" />
            Maracás — Bahia
          </motion.div>

          <h1 className="font-display text-5xl font-extrabold leading-[0.92] tracking-tighter text-maraca-navy sm:text-6xl lg:text-7xl">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden py-1">
                <motion.span custom={i} variants={lineReveal} initial="hidden" animate="show" className="block">
                  {line}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden py-1">
              <motion.span custom={2} variants={lineReveal} initial="hidden" animate="show" className="block text-maraca-teal">
                Peça em minutos.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-6 max-w-md text-lg leading-relaxed text-maraca-navy/70"
          >
            Comida, mercado, farmácia, açaí e muito mais entregues na sua porta.
            Baixe o app do Delivery Maracás e receba tudo com rapidez.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.85 }}
          >
            <AppBadges className="mt-8" />
            <div className="mt-6 flex items-center gap-6 text-sm text-maraca-navy/60">
              <span className="flex items-center gap-1.5">
                <Star size={18} weight="fill" className="text-maraca-amber" /> 4.9 avaliação
              </span>
              <span className="flex items-center gap-1.5">
                <Lightning size={18} weight="fill" className="text-maraca-pink" /> Entrega expressa
              </span>
            </div>
          </motion.div>
        </div>

        <motion.div style={{ y }} className="relative mx-auto flex w-full max-w-sm justify-center">
          <div className="relative aspect-[9/18] w-[280px] rounded-[2.75rem] border-[10px] border-maraca-navy bg-maraca-navy shadow-2xl">
            <div className="h-full w-full overflow-hidden rounded-[2rem] bg-white">
              <img
                src="/app-tela.jpeg"
                alt="Tela do app Delivery Maracás"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          {chips.map((c) => (
            <motion.div
              key={c.label}
              className={`absolute rounded-2xl px-4 py-2 font-display text-sm font-bold text-maraca-navy shadow-lg ${c.color}`}
              style={{ top: c.top, bottom: c.bottom, left: c.left, right: c.right }}
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: c.delay }}
            >
              {c.label}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
